import { Effect } from 'effect';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { sessionPageEffect } from '../client';
import { useRequestOwner } from './use-request-owner';
import type { OkraSession, Page } from '../types';
import { okraHookFailure, runOkraHookEffect, type OkraHookError } from './hook-io';
import { useOkra } from './provider';

export interface UsePageContentOptions {
  /** Poll interval while content not yet ready (default: 3000). 0 to disable. */
  pollInterval?: number;
}

export interface UsePageContentReturn {
  data: Page | null;
  content: string;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Native single-page program — the IO seam behind {@link usePageContent}.
 *
 * Page markdown/blocks never enter the failure payload; only the operation
 * label travels with the tagged error.
 */
export function fetchPageContentEffect(
  session: OkraSession,
  pageNumber: number,
  signal?: AbortSignal,
): Effect.Effect<Page, OkraHookError> {
  return sessionPageEffect(session, pageNumber, signal).pipe(
    Effect.mapError((error) =>
      okraHookFailure('pageContent', 'Page content could not be fetched', error.cause),
    ),
  );
}

/**
 * Fetch a single page's content (markdown + blocks + entities).
 * Polls until content arrives if the page is still processing.
 *
 * ```tsx
 * const { content, data, isLoading } = usePageContent(session, 1);
 * ```
 */
export function usePageContent(
  sessionOrId: OkraSession | string | null,
  pageNumber: number,
  options: UsePageContentOptions = {},
): UsePageContentReturn {
  const { pollInterval = 3000 } = options;
  const { client } = useOkra();

  const [data, setData] = useState<Page | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const session = useMemo(
    () => (typeof sessionOrId === 'string' ? client.sessions.from(sessionOrId) : sessionOrId),
    [client, sessionOrId],
  );
  const owner = useRequestOwner(session, pageNumber);

  const fetchPage = useCallback(async () => {
    if (!session || !pageNumber) return;
    const ac = owner.begin();
    if (!ac) return;
    try {
      setIsLoading(true);
      const result = await runOkraHookEffect(
        fetchPageContentEffect(session, pageNumber),
        ac.signal,
      );
      if (!owner.owns(ac)) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (!owner.owns(ac) || (err as Error).name === 'AbortError') return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (owner.owns(ac)) setIsLoading(false);
    }
  }, [session, pageNumber, owner]);

  // Reset when the selected session or page changes
  useEffect(() => {
    setData(null);
    setError(null);
  }, [session, pageNumber]);

  // Initial fetch
  useEffect(() => {
    setIsLoading(false);
    void fetchPage();
  }, [fetchPage]);

  // Poll until content arrives
  useEffect(() => {
    if (!session || !pageNumber || !pollInterval) return;
    if (data?.content) return;

    const interval = setInterval(fetchPage, pollInterval);
    return () => clearInterval(interval);
  }, [session, pageNumber, pollInterval, fetchPage, Boolean(data?.content)]);

  return {
    data,
    content: data?.content ?? '',
    isLoading,
    error,
    refetch: fetchPage,
  };
}
